import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { CheckCircleIcon, XCircleIcon, ArrowPathIcon, ArrowTopRightOnSquareIcon, DocumentTextIcon } from '@heroicons/react/24/outline'
import { storePaymentsAPI } from '../services/api'
import { t } from '../i18n'

export const PROOF_BADGES = {
  pending: ['Receipt to verify', 'bg-amber-100 text-amber-800'],
  verified: ['Transfer verified', 'bg-green-100 text-green-800'],
  rejected: ['Receipt rejected', 'bg-red-100 text-red-800'],
}

// Order details: the customer's bank-transfer receipt (fetched with the
// staff token - receipts are not public files) with Approve / Reject, or a
// "check Stripe" button for unpaid card orders.
export default function PaymentProofReview({ order, onChange }) {
  const [file, setFile] = useState(null) // { url, type }
  const [loadingFile, setLoadingFile] = useState(false)
  const [busy, setBusy] = useState(false)
  const [rejecting, setRejecting] = useState(false)
  const [note, setNote] = useState('')

  useEffect(() => {
    if (order.payment_method !== 'bank_transfer' || !order.payment_proof_status) return
    let url
    setLoadingFile(true)
    storePaymentsAPI.getProof(order.id)
      .then(({ data }) => { url = URL.createObjectURL(data); setFile({ url, type: data.type }) })
      .catch(() => setFile(null))
      .finally(() => setLoadingFile(false))
    return () => { if (url) URL.revokeObjectURL(url) }
  }, [order.id, order.payment_method, order.payment_proof_status, order.payment_proof_uploaded_at])

  const review = async decision => {
    setBusy(true)
    try {
      const { data } = await storePaymentsAPI.reviewProof(order.id, decision, note)
      toast.success(decision === 'approve' ? t("Payment verified - order marked as paid") : t("Receipt rejected - the customer can upload a new one"))
      setRejecting(false); setNote('')
      onChange?.({ ...order, ...data })
    } catch (err) {
      toast.error(t(err.response?.data?.error || 'Could not update the payment'))
    } finally { setBusy(false) }
  }

  const refreshCard = async () => {
    setBusy(true)
    try {
      const { data } = await storePaymentsAPI.refreshCard(order.id)
      if (data.paid) { toast.success(t("Stripe confirms the card payment")); onChange?.({ ...order, payment_status: 'PAID' }) }
      else toast(t("Stripe has no completed payment for this order yet"), { icon: 'ℹ️' })
    } catch (err) {
      toast.error(t(err.response?.data?.error || 'Could not reach Stripe'))
    } finally { setBusy(false) }
  }

  if (order.payment_method === 'card') {
    if (order.payment_status === 'PAID') return null
    return (
      <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 mb-6 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-indigo-900">{t("Card order - the customer hasn't completed the Stripe payment yet.")}</p>
        <button onClick={refreshCard} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold disabled:opacity-50">
          <ArrowPathIcon className="h-4 w-4" /> {t("Check payment with Stripe")}
        </button>
      </div>
    )
  }

  if (order.payment_method !== 'bank_transfer') return null
  const badge = PROOF_BADGES[order.payment_proof_status]

  return (
    <section className="border border-gray-200 rounded-xl p-5 mb-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="font-bold text-gray-900 text-lg">{t("Bank transfer receipt")}</h3>
        {badge ? <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badge[1]}`}>{badge[0]}</span>
          : <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">{t("Waiting for the customer to upload")}</span>}
      </div>

      {!order.payment_proof_status && <p className="text-sm text-gray-600">{t("The customer chose bank transfer. Their receipt will appear here as soon as they upload it.")}</p>}

      {order.payment_proof_status && (
        <div className="grid md:grid-cols-[minmax(0,1fr)_260px] gap-5 items-start">
          <div className="bg-gray-50 rounded-lg border border-gray-200 min-h-[200px] grid place-items-center overflow-hidden">
            {loadingFile ? <p className="text-sm text-gray-500">{t("Loading receipt…")}</p>
              : !file ? <p className="text-sm text-gray-500">{t("The receipt could not be loaded.")}</p>
              : file.type === 'application/pdf'
                ? <div className="text-center p-6"><DocumentTextIcon className="h-12 w-12 mx-auto text-gray-400" /><p className="text-sm text-gray-600 mt-2">{t("PDF receipt")}</p></div>
                : <img src={file.url} alt={t("Customer's transfer receipt")} className="max-h-[420px] w-full object-contain" />}
          </div>
          <div className="space-y-3">
            {file && <a href={file.url} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 w-full py-2.5 rounded-lg border border-gray-300 text-sm font-medium hover:bg-gray-50">
              <ArrowTopRightOnSquareIcon className="h-4 w-4" /> {t("Open full size")}
            </a>}
            <p className="text-sm text-gray-600">{t("Check the amount (")}<strong>{Number(order.total).toFixed(2)}</strong>{t(") and that it arrived in your bank account before approving.")}</p>
            {order.payment_proof_status === 'rejected' && order.payment_proof_note && <p className="text-sm text-red-700">{t("Reason given: “")}{order.payment_proof_note}”</p>}
            {order.payment_proof_status !== 'verified' && (rejecting ? (
              <div className="space-y-2">
                <label htmlFor="reject-note" className="text-sm font-medium text-gray-700">{t("Reason (shown to the customer)")}</label>
                <textarea id="reject-note" rows={2} maxLength={255} value={note} onChange={e => setNote(e.target.value)} className="form-input" placeholder={t("e.g. The amount doesn't match the order total")} />
                <div className="flex gap-2">
                  <button onClick={() => review('reject')} disabled={busy} className="flex-1 py-2.5 rounded-lg bg-red-600 text-white text-sm font-semibold disabled:opacity-50">{t("Reject receipt")}</button>
                  <button onClick={() => setRejecting(false)} className="px-4 py-2.5 rounded-lg bg-gray-100 text-sm">{t("Cancel")}</button>
                </div>
              </div>
            ) : (
              <div className="flex gap-2">
                <button onClick={() => review('approve')} disabled={busy} className="flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-semibold disabled:opacity-50">
                  <CheckCircleIcon className="h-5 w-5" /> {t("Approve")}
                </button>
                <button onClick={() => setRejecting(true)} disabled={busy} className="flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-700 text-sm font-semibold">
                  <XCircleIcon className="h-5 w-5" /> {t("Reject")}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  )
}
